import React, {useMemo, useState} from 'react'
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
} from 'chart.js'
import {Pie, Bar} from 'react-chartjs-2'

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend)

interface MatrixQuestionChartSwitcherProps {
    surveyJson: any
    surveyData: any[]
}

const CHART_COLORS = [
    '#4e79a7',
    '#f28e2c',
    '#e15759',
    '#76b7b2',
    '#59a14f',
    '#edc949',
    '#af7aa1',
    '#ff9da7',
    '#9c755f',
    '#bab0ac',
]

const resolveText = (source: any): string => {
    if (!source) {
        return ''
    }

    if (typeof source === 'string') {
        return source
    }

    if (typeof source === 'object') {
        if (typeof source.tr === 'string') {
            return source.tr
        }

        const values = Object.values(source)
        if (values.length > 0 && typeof values[0] === 'string') {
            return values[0] as string
        }
    }

    return ''
}

const getDisplayText = (item: any, fallback: string) => {
    if (!item) {
        return fallback
    }

    if (typeof item === 'string') {
        return item
    }

    if (item.title) {
        const localized = resolveText(item.title)
        if (localized) {
            return localized
        }
    }

    if (item.text) {
        const localized = resolveText(item.text)
        if (localized) {
            return localized
        }
    }

    if (item.name) {
        return item.name
    }

    return fallback
}

const MatrixQuestionChartSwitcher: React.FC<MatrixQuestionChartSwitcherProps> = ({surveyJson, surveyData}) => {
    const matrixQuestions = useMemo(() => {
        if (!surveyJson || typeof surveyJson !== 'object') {
            return []
        }

        const pages = Array.isArray(surveyJson.pages) ? surveyJson.pages : []

        return pages
            .flatMap((page: any) => (Array.isArray(page?.elements) ? page.elements : []))
            .filter((element: any) => element && element.type === 'matrix' && Array.isArray(element.rows) && Array.isArray(element.columns))
    }, [surveyJson])

    const sanitizedData = useMemo(
        () => (Array.isArray(surveyData) ? surveyData.filter((entry: any) => entry && typeof entry === 'object') : []),
        [surveyData]
    )

    const [chartTypes, setChartTypes] = useState<Record<string, 'bar' | 'pie'>>({})

    const handleChartTypeChange = (key: string, type: 'bar' | 'pie') => {
        setChartTypes((prev) => ({
            ...prev,
            [key]: type,
        }))
    }

    if (matrixQuestions.length === 0) {
        return null
    }

    return (
        <div className='card mb-10'>
            <div className='card-header'>
                <h3 className='card-title mb-0'>Matris Soruları - Pasta / Bar Grafikleri</h3>
            </div>
            <div className='card-body'>
                {matrixQuestions.map((question: any) => {
                    const questionTitle = getDisplayText(question, question?.name || '')
                    const columns = Array.isArray(question?.columns) ? question.columns : []
                    const rows = Array.isArray(question?.rows) ? question.rows : []
                    const normalizedRows = rows
                        .map((row: any) => {
                            if (typeof row === 'string') {
                                return {value: row, text: row}
                            }

                            if (row && typeof row === 'object') {
                                return row
                            }

                            return null
                        })
                        .filter((row: any): row is Record<string, any> => !!row)

                    if (columns.length === 0 || normalizedRows.length === 0) {
                        return null
                    }

                    return (
                        <div key={question.name} className='mb-10'>
                            <h4 className='fw-bold mb-5'>{questionTitle}</h4>
                            {normalizedRows.map((row: any) => {
                                    const rowValue = row?.value
                                    const rowKey = `${question.name}__${rowValue}`
                                    const chartType = chartTypes[rowKey] || 'bar'
                                    const rowTitle = getDisplayText(row, rowValue)

                                    const answers = sanitizedData
                                        .map((entry: any) => entry?.[question.name]?.[rowValue])
                                        .filter((value: any) => value !== undefined && value !== null && `${value}`.length > 0)

                                    if (answers.length === 0) {
                                        return (
                                            <div key={rowKey} className='border rounded p-5 mb-5 bg-light text-muted'>
                                                <div className='fw-semibold mb-2'>{rowTitle}</div>
                                                <div>Bu soru için cevap bulunmuyor.</div>
                                            </div>
                                        )
                                    }

                                    const labels = columns.map((column: any) => getDisplayText(column, column?.value))
                                    const counts = columns.map((column: any) =>
                                        answers.filter((value: any) => `${value}` === `${column?.value}`).length
                                    )
                                    const palette = columns.map(
                                        (_: any, index: number) => CHART_COLORS[index % CHART_COLORS.length]
                                    )

                                    const likertWeights = columns.map((column: any, index: number) => {
                                        const rawValue = column?.value

                                        if (typeof rawValue === 'number' && !Number.isNaN(rawValue)) {
                                            return rawValue
                                        }

                                        if (typeof rawValue === 'string') {
                                            const numericMatch = rawValue.match(/\d+/)

                                            if (numericMatch) {
                                                const parsed = parseInt(numericMatch[0], 10)

                                                if (!Number.isNaN(parsed)) {
                                                    return parsed
                                                }
                                            }
                                        }

                                        return index + 1
                                    })

                                    const totalAnswers = counts.reduce((acc: number, current: number) => acc + current, 0)
                                    const weightedSum = counts.reduce(
                                        (acc: number, current: number, idx: number) => acc + current * likertWeights[idx],
                                        0
                                    )
                                    const likertAverage = totalAnswers > 0 ? weightedSum / totalAnswers : null

                                    const data = {
                                        labels,
                                        datasets: [
                                            {
                                                label: 'Cevap Sayısı',
                                                data: counts,
                                                backgroundColor: palette,
                                                borderColor: palette,
                                                borderWidth: 1,
                                            },
                                        ],
                                    }

                                    const commonOptions: any = {
                                        plugins: {
                                            legend: {
                                                position: 'top',
                                            },
                                            tooltip: {
                                                callbacks: {
                                                    label: (context: any) => `${context.label}: ${context.parsed}`,
                                                },
                                            },
                                        },
                                    }

                                    const barOptions: any = {
                                        ...commonOptions,
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        scales: {
                                            y: {
                                                beginAtZero: true,
                                                ticks: {
                                                    precision: 0,
                                                },
                                            },
                                        },
                                    }

                                    const pieOptions: any = {
                                        ...commonOptions,
                                        responsive: true,
                                        maintainAspectRatio: false,
                                    }

                                    return (
                                        <div key={rowKey} className='border rounded p-5 mb-5'>
                                            <div className='d-flex flex-column flex-sm-row align-items-sm-center justify-content-between mb-4 gap-3'>
                                                <div>
                                                    <div className='fw-semibold'>{rowTitle}</div>
                                                <div className='text-muted small'>Toplam cevap: {answers.length}</div>
                                                <div className='text-muted small'>
                                                    Likert ortalaması:{' '}
                                                    {likertAverage !== null ? likertAverage.toFixed(2) : '-'}
                                                </div>
                                                </div>
                                                <div className='btn-group btn-group-sm'>
                                                    <button
                                                        type='button'
                                                        className={`btn ${chartType === 'bar' ? 'btn-primary' : 'btn-light-primary'}`}
                                                        onClick={() => handleChartTypeChange(rowKey, 'bar')}
                                                    >
                                                        Bar Grafik
                                                    </button>
                                                    <button
                                                        type='button'
                                                        className={`btn ${chartType === 'pie' ? 'btn-primary' : 'btn-light-primary'}`}
                                                        onClick={() => handleChartTypeChange(rowKey, 'pie')}
                                                    >
                                                        Pasta Grafik
                                                    </button>
                                                </div>
                                            </div>
                                            <div style={{height: '320px'}}>
                                                {chartType === 'pie' ? (
                                                    <Pie data={data} options={pieOptions} />
                                                ) : (
                                                    <Bar data={data} options={barOptions} />
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export default MatrixQuestionChartSwitcher
